import React, { useEffect } from "react";
import { useMediaQuery } from 'react-responsive';
import { Configure, InstantSearch } from "react-instantsearch-dom";
import qs from "qs";
import { searchClient } from "~/src/lib/algolia";
import { Refinement } from "./Refinement";
import * as classes from "./ProductListingPage.module.css";
import { ProductContainer } from "./ProductContainer";
import { StickyBoundary } from "~src/utils/Sticky"; 
import isEqual from "lodash/isEqual"; 
import pick from "lodash/pick";
import { Summary } from "./Summary"
import { QuizCollection } from "../QuizCollection"; 
 
export type PLPContextValue = { 
  handle: string | null; 
  query: string | null; 
  defaultSortBy:
    | "manual"
    | "best-selling"
    | "title-ascending"
    | "title-descending"
    | "price-ascending"
    | "price-descending"
    | "created-ascending"
    | "created-descending"; 
  bundleFunctions: any; 
  collection: any; 
  quiz: any;
  json: any; 
  utilities: any;
  highestPriceProduct: any;
  lowestPriceProduct: any;
  customerTags: any;
  showOutOfStock: any;
  visual_percentage_off_tab: any;
  b2b: any;
  refinementToggle: any;
  setRefinementToggle: any;
}; 
 
export const PLPContext = React.createContext({} as any);

export const usePLPContext = () =>
  React.useContext<PLPContextValue>(PLPContext); 
  
export type ShopifyMedia = {
  id: number;
  alt: string;
  aspect_ratio: number;
  preview_image: string;
} & (
  | {
      media_type: "video";
      value: {
        sources: Array<{
          format: string;
          height: number;
          width: number;
          mime_type: string;
          url: string;
        }>;
      };
    }
  | {
      media_type: "image";
      value: string;
    }
);
 
export type ShopifyAdvert = {
  creative: ShopifyMedia;
  link: string;
  campaign_name: string;
  campaign_id: string;
  creative_name: string;
  aspect_ratio: number;
};

const productsPrefix = "shopify_products";

type ProductListingPageProps = {
  userToken: string;
  query: string;
  title: string;
  handle: string;
  tagFilters: string[];
  defaultSortBy:
    | "manual"
    | "best-selling"
    | "title-ascending"
    | "title-descending"
    | "price-ascending"
    | "price-descending"
    | "created-ascending"
    | "created-descending";
  adverts: ShopifyAdvert[];
  bubbleFilters: boolean;
  bundleType: string,
  bundleKey: string,
  athleteCollectionTag: string,
  enableWishlistTrap: boolean,
  bogoBundleValue: string,
  fixedPriceBundleValue: string,
  bundleSetCount: string,
  bundleOfferText: string,
  tabName: string;
  isQuiz: boolean;
  isCollectionTabs: boolean;
  highestPriceProduct: any;
  lowestPriceProduct: any;
  b2b: string;
  customerTags: string[];
  showOutOfStock: boolean;
  visual_percentage_off_tab: any;
};


export const indexMap = {
  default: [process.env.REACT_APP_ALGOLIA_PREFIX, productsPrefix].join("_"),
  "price-ascending": [
    process.env.REACT_APP_ALGOLIA_PREFIX,
    productsPrefix,
    "price_asc",
  ].join("_"),
  "price-descending": [
    process.env.REACT_APP_ALGOLIA_PREFIX,
    productsPrefix,
    "price_desc",
  ].join("_"),
  "created-descending": [
    process.env.REACT_APP_ALGOLIA_PREFIX,
    productsPrefix,
    "newest",
  ].join("_"),
};
   
const sortByToIndex = (sortBy: string): string =>
  sortBy in indexMap ? indexMap[sortBy] : indexMap["default"];

const indexToSortBy = (index: string) => {
  const invertedMap = Object.entries(indexMap).reduce((obj, [key, value]) => {
    obj[value] = key;
    return obj;
  }, {});

  return index in invertedMap ? invertedMap[index] : "default";
};

export const ProductListingPage = ({
  tagFilters = [],
  query = '',
  handle,
  defaultSortBy,
  userToken,
  adverts,
  tabName,
  bubbleFilters,
  bundleType = 'no_bundle',
  bundleKey,
  bundleOfferText,
  athleteCollectionTag,
  enableWishlistTrap = false,
  bogoBundleValue,
  fixedPriceBundleValue,
  bundleSetCount,
  isQuiz = false,
  isCollectionTabs = false,
  highestPriceProduct,
  lowestPriceProduct,
  b2b = '',
  customerTags = [],
  showOutOfStock = false,
  visual_percentage_off_tab = 0,
  json = ''
}: ProductListingPageProps) => { 
  const isDesktopViewport = useMediaQuery({ query: '(min-width: 768px)' });
  const rootRef = React.useRef(null);
  const previousSearchStateRef = React.useRef(null);
  const optionalFilters = React.useMemo(() => {
    const parsed = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    }); 

    const { boost } = parsed;
    if (typeof boost === "string") return boost.split(";").join(" AND ");
  }, []);

  // Check for Quiz answers
  const [quizAnswers, setQuizAnswers] = React.useState([])
  const [quizSubmittedOrSkipped, setQuizSubmittedOrSkipped] = React.useState(false)

  const [searchState, setSearchState] = React.useState<any>(() => {
    let searchQuery = {}

    if(isCollectionTabs){
      const preferedTab = qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
      }).tab
      
        tabName == preferedTab ? searchQuery = window.location.search : searchQuery = {"page": "1", "sortBy": "default"};

    } else {
      searchQuery = window.location.search
    }

    
    const parsed = qs.parse(searchQuery, {
      ignoreQueryPrefix: true,
    });
    // const { page, sortBy = defaultSortBy, refine, toggle } = parsed;
    let { page, sortBy = defaultSortBy, refine, toggle, range } = parsed;

    // Automatically add toggle value if B2B available.
    if(b2b){
      if(toggle && toggle[b2b] == undefined)
        toggle = {...toggle, ...{[b2b]: 'true'}} //toggle.push({'facets.Bras': true})
      else if(toggle && toggle[b2b])
        toggle[b2b] = true
      else
        toggle = {[b2b]: 'true'}
    }

    return {
      page: typeof page === "string" ? Number(page) : 1,
      sortBy:
        (typeof sortBy === "string" && sortByToIndex(sortBy)) ||
        sortByToIndex("default"),
      refinementList: refine != undefined ? Object.fromEntries( Object.entries(refine).filter(([key, value]) => value !== '') ) : {},
      toggle,
      range: range != undefined ? range : undefined
    }; 
  });

  const [refinementToggle, setRefinementToggle] = React.useState({
    boolean: searchState?.toggle != undefined ? searchState?.toggle : false,
    ...searchState?.refinementList
  })
  // const [energyDrinkLimit, setEnergyDrinkLimit] = React.useState(false)

  // Energy Drink limit checker
  // Current Cart Items
  // const checkCart = async () => { 
  //   fetch('/cart.js')
  //   .then(response => response.json())
  //   .then(async (cart) => {
  //     let items = cart.items;
  //     let totalQuantity = 0;
  //     items.forEach(item => {
  //       if(item.product_type == "Energy Drink"){
  //         totalQuantity = totalQuantity + item.quantity
  //       }
  //     })
  //     if(totalQuantity >= 2){
  //       // setEnergyDrinkLimit(true)
  //     }
  //   })
  // }

  // const [bundleItem, setBundleState] = React.useState([]);
  const [bundleProducts, setBundleProducts] = React.useState([])
  const bundleMax = parseInt(bundleSetCount)
  let discountAmount = 0; 


  //Unsure if I need to parse the bundleDiscountValue coming from the bundle-offer.liquid section
  if (bundleType == "bogo"){
    discountAmount = parseFloat(bogoBundleValue)
  } else if (bundleType == "fixed-price-bundle" || bundleType == "rollover-bundle"){
    discountAmount = parseFloat(fixedPriceBundleValue)
  }
  
  const bundleDiscount = discountAmount

  const [bundleCount, setBundleCount] = React.useState(0)
  // const [bundleCanAddMore, setBundleCanAddMore] = React.useState(true)
  const [triggerSummary, setTriggerSummary] = React.useState(false)
  const [lowestProduct, setLowestProduct] = React.useState([])
  const [totalSaved, setTotalSaved] = React.useState(0)
  // const [totalBundlePrice, setTotalBundlePrice] = React.useState(0)

  const currentMax = () => {
    return bundleCount < bundleMax ? bundleMax : Math.ceil(bundleCount / bundleMax) * bundleMax
  }

  // const updateTotalBundlePrice = (item) => {
  //   setTotalBundlePrice(totalBundlePrice + parseFloat(item.price));
  // }

  const clearBundleProducts = () => {
    setBundleProducts([])
    setLowestProduct([])
    setBundleCount(0)
  }

  const handleTriggerSummary = () => {
    setTimeout(()=> {
      setTriggerSummary(!triggerSummary)
      // setTimeout(()=> {
      //   setTriggerSummary(!triggerSummary)
      // },200)
    },850)
  }

  const bundleCanAddMore = () => {
    // return bundleCount < bundleMax
    return true 
  }

  const productVariantInBundle = (prod_id) => {
    return bundleProducts.find(ele => ele.product_id == prod_id) != undefined
  }
  
  const updateBundleProducts = (arr) => {
    const tmpBundles = bundleProducts

    if(arr){ 
      arr.forEach(item => {
        //Track total bundle price
        const exists = tmpBundles.find(el => el.id == item.id)
        const existsIndex = tmpBundles.indexOf(exists)
        if(exists && exists.quantity > 0){ // Update count.
          tmpBundles[existsIndex].quantity = exists.quantity
        }else if(exists && item.quantity == 0){ // Remove product from bundle if 0 counted.
          tmpBundles.splice(existsIndex, 1)
        }else if(item.quantity > 0){ // Add item if not in found in bundle yet.
          tmpBundles.push(item)
        }
      });
    }
    let productCount = 0
    tmpBundles.forEach(item => {
      productCount = productCount + item.quantity;
    })
    setBundleProducts(tmpBundles)
    setBundleCount(productCount)
    handleLowestProduct()
  }
  const handleBundleCount = () => {
    let productCount = 0
    if(bundleProducts.length){
      bundleProducts.forEach(item => {
        productCount = productCount + item.quantity;
      });
    }
    setBundleCount(productCount)
  }

  React.useEffect(() => {
    handleBundleCount();
  },[bundleProducts])

  React.useEffect(()=>{
    setTimeout(()=> {
      if(bundleCount % bundleMax === 0){ 
        handleTriggerSummary();
      }
    },850)
  },[bundleCount])

/* Find lowest product */
  const handleLowestProduct = () => {
    let lowest = []

    setTotalSaved(0)
    setLowestProduct([])

    // BOGO Style discount logic
    if(bundleType == "bogo"){
      if(bundleCount >= bundleMax){
        let currentMaxItems = Math.floor(bundleCount / bundleMax) //currentMax() - bundleCount == 0 ? bundleCount : (bundleCount - (currentMax() - bundleCount))
        let ttlSaved = 0

        bundleProducts.forEach(prod => {
          let quantityToDiscount = 0
          
          if(currentMaxItems > 0){
            if(currentMaxItems >= prod.quantity){
              currentMaxItems = currentMaxItems - prod.quantity
              quantityToDiscount = prod.quantity
            }else{
              quantityToDiscount = prod.quantity - currentMaxItems
              currentMaxItems = currentMaxItems - prod.quantity <= 0 ? 0 : prod.quantity - currentMaxItems
            }
            prod.quantityToDiscount = quantityToDiscount
            prod.saved = parseFloat(prod.price) - parseFloat(prod.price) * (1 - bundleDiscount)
            ttlSaved = ttlSaved + (prod.saved * prod.quantityToDiscount)
            lowest.push(prod)
          }
        })
        
        setTotalSaved(ttlSaved)
        setLowestProduct(lowest)
      }
    } else if(bundleType == "fixed-price-bundle"){
      if (bundleCount >= bundleMax){
        let currentMaxItems = currentMax() - bundleCount == 0 ? bundleCount : (bundleCount - (currentMax() - bundleCount))
        let ttlSaved = 0
        let totalPriceAfterDiscounts = 0

        bundleProducts.forEach(prod => {
          let quantityToDiscount = 0

          if(currentMaxItems){
            if(currentMaxItems >= prod.quantity){
              quantityToDiscount = prod.quantity
              currentMaxItems = currentMaxItems - prod.quantity
            } else {
              quantityToDiscount = prod.quantity - currentMaxItems
              currentMaxItems = currentMaxItems <= 0 ? 0 : prod.quantity - currentMaxItems
            }
            totalPriceAfterDiscounts = (parseFloat(prod.price) - parseFloat(prod.saved))
            prod.saved = prod.price - parseFloat(bundleDiscount / bundleMax);
            prod.quantityToDiscount = quantityToDiscount
            ttlSaved = ttlSaved + (prod.saved * prod.quantityToDiscount)
            lowest.push(prod)
          }
        })

        //Pass this amount through to the globally managed state
        setTotalSaved(ttlSaved)
        setLowestProduct(lowest);
      }
    } else if(bundleType == "rollover-bundle"){
      if (bundleCount >= parseInt(bundleSetCount)){
        let ttlSaved = 0
        let totalPriceAfterDiscounts = 0 // This may be useless

        bundleProducts.forEach(prod => {
          let quantityToDiscount = 0
          quantityToDiscount = prod.quantity
          
          totalPriceAfterDiscounts = (parseFloat(prod.price) - parseFloat(prod.saved)) // This may be useless
          prod.saved = prod.price * (parseFloat(bundleDiscount)/100);
          prod.quantityToDiscount = quantityToDiscount
          ttlSaved = ttlSaved + (prod.saved * prod.quantityToDiscount)
          lowest.push(prod)
        })

        //Pass this amount through to the globally managed state
        setTotalSaved(ttlSaved)
        setLowestProduct(lowest);
      }
    }

  }

  /* This needs work */
  const removeFromBundle = (product) => {
    let TMPbundle = bundleProducts
    let currentSaved = 0
    TMPbundle.forEach((prod, i) => { 
      if(prod.id == product.id){
        product.saved = parseFloat("0.00");
        prod.quantity = prod.quantity - 1;
      }

    })
    setBundleProducts(TMPbundle.filter(prod => prod.quantity > 0))
    handleLowestProduct()
  }

  
  /*
   * This code is to apply a 'load-more' feature to desktop devices
   * by manipulating the URL with get variables.
   * Only works on non-collection-tab collections.
   */ 
  React.useEffect(() => {
    if(isDesktopViewport && !isCollectionTabs){
      const previousQuery = qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
      });
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${qs.stringify(
          {
            ...previousQuery,
            page: searchState.page,
            sortBy: indexToSortBy(searchState.sortBy),
            refine: searchState.refinementList != undefined ? Object.fromEntries( Object.entries(searchState.refinementList).filter(([key, value]) => value !== '') ) : undefined,
            toggle: searchState.toggle,
          },
          {
            encodeValuesOnly: true,
          }
        )}`
      );
    }
  }, [searchState]); 
 
  
  React.useEffect(() => {
    // checkCart(); 
    if (previousSearchStateRef.current) {
      const previousSearchState = previousSearchStateRef.current;

      const pathsToCompare = ["sortBy", "refinementList", "toggle"];
 
      if (
        !isEqual(
          pick(previousSearchState, pathsToCompare),
          pick(searchState, pathsToCompare)
        )
      ) {

        const top = isCollectionTabs ? document.querySelector('#collection-tabs-anchor').getBoundingClientRect().y + document.querySelector('#collection-tabs-anchor').getBoundingClientRect().height : document.querySelector(".collection__info h1") ? document.querySelector(".collection__info h1").getBoundingClientRect().y + document.querySelector(".collection__info h1").getBoundingClientRect().height : 0
        
        document
          .querySelector("sticky-header")
          .dispatchEvent(new CustomEvent("preventHeaderReveal"));
        window.scrollBy({ top });
      }
    }
    
    return () => {
      previousSearchStateRef.current = searchState;
    };
  }, [searchState]);

  const filters = [];
  
  if (handle) {
    filters.push(`smartCollectionHandles:${handle}`);
  }
  filters.push(`is_published:true`)
  if(!showOutOfStock)
    filters.push(`total_units > 0`) 

  const athleteTag = athleteCollectionTag ? athleteCollectionTag : false

  const forceWishlist = enableWishlistTrap ? enableWishlistTrap : false;

  const womensCollection = handle != null && (handle.includes("womens") || handle.includes("graphic-tees")) ? true : false

  const buffer = isCollectionTabs ? -38 : 0;

  /*
   * Filter Query that will update the listing output.
   */
  const filterQuery = filters.join(" AND ")


  return (
    <> 
      <PLPContext.Provider value={{ 
        searchState,
        setSearchState,
        refinementToggle,
        setRefinementToggle,
        handle,
        query,
        defaultSortBy,
        b2b,
        visual_percentage_off_tab,
        customerTags,
        collection: {
          athleteTag,
          forceWishlist,
          womensCollection
        },
        bundleFunctions: {
          // totalBundlePrice,
          bundleProducts,
          bundleType,
          bundleKey,
          bundleMax,
          bundleCount,
          bundleCanAddMore,
          updateBundleProducts,
          // handleBundleCanAddMore,
          productVariantInBundle,
          removeFromBundle,
          triggerSummary,
          handleTriggerSummary,
          setTriggerSummary,
          handleLowestProduct,
          clearBundleProducts,
          lowestProduct,
          currentMax,
          bundleDiscount,
          totalSaved,
          bundleOfferText
        },
        quiz: {
          isQuiz,
          quizAnswers,
          setQuizAnswers, 
          quizSubmittedOrSkipped,
          setQuizSubmittedOrSkipped
        }
      }}>
        {isQuiz && (
          <QuizCollection  
            json={json} 
          /> 
        )}
        {isQuiz && quizAnswers.length == json.questions.length && quizSubmittedOrSkipped || !isQuiz ? (
          <InstantSearch
            indexName={indexMap["default"]}
            searchClient={searchClient}
            searchState={searchState}
            onSearchStateChange={setSearchState}
          > 
            <Configure
              query={query}
              optionalFilters={optionalFilters}
              filters={filterQuery}
              tagFilters={tagFilters}
              hitsPerPage={48}
              clickAnalytics={true}
              enableRules
              userToken={userToken || undefined}
              analyticsTags={[handle, ...tagFilters]}
              ruleContexts={[handle]}
            /> 
            <div ref={rootRef} className={classes.root + ' component__productListingPage'}>
              <StickyBoundary buffer={buffer}>
                <div className="container">
                  {/* {!isQuiz && ( */}
                    <Refinement handle={handle} isCollectionTabs={isCollectionTabs} bubbleFilters={bubbleFilters} tabName={tabName} setSearchState={setSearchState} highestPriceProduct={highestPriceProduct} lowestPriceProduct={lowestPriceProduct} />
                  {/* )} */}
                  <ProductContainer 
                    handle={handle}
                    adverts={adverts} 
                  />
                </div>
                {bundleType != undefined && bundleType != 'no_bundle' && (
                  <Summary
                    bundle={bundleProducts}
                    offerText={bundleOfferText}
                  />
                )}
                
              </StickyBoundary>
            </div>
          </InstantSearch>
        ) : ( 
          <></> 
        )}

      </PLPContext.Provider>
 
    </>
  );
};
  